import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Wrench, ArrowLeft, Plus, Pencil, Trash2, Search, Save, X, Upload } from "lucide-react";
import EmployeeEditor from "./EmployeeEditor";
import { CatalogEditor } from "./CatalogEditor";
import { ColourEditor } from "./ColourEditor";
import { PARTS_CFG, SAUNAS_CFG, GAZEBOS_CFG } from "./catalogConfigs";
import SpaEditor from "../showroom/SpaEditor";
import DocumentScripts from "../company/DocumentScripts";

const FORMS = [
  ["spas", "Add / Edit Spas", "#1a4f8a"], ["parts", "Add / Edit Parts", "#2e5a1e"], ["saunas", "Add / Edit Saunas", "#7a4a12"],
  ["gazebos", "Add / Edit Gazebos", "#0f5a5a"], ["colours", "Add / Edit Colours", "#7a1220"],
];

function FormPopup({ id, onClose }) {
  if (id === "spas") return (
    <div className="fixed inset-0 z-[66] bg-slate-900/60 flex items-center justify-center p-3" data-testid="ce-spas">
      <div className="w-full max-w-7xl max-h-[92vh] overflow-y-auto bg-white rounded-lg shadow-2xl"><SpaEditor onExit={onClose} /></div>
    </div>
  );
  if (id === "colours") return <ColourEditor onClose={onClose} />;
  return <CatalogEditor cfg={{ parts: PARTS_CFG, saunas: SAUNAS_CFG, gazebos: GAZEBOS_CFG }[id]} onClose={onClose} />;
}

const TILE_COLORS = [
  "#123a63", "#2f5fae", "#7a4a12", "#2e5a1e", "#7a1220", "#4a2f7a", "#0f5a5a", "#8a5a00",
];

async function uploadImage(file) {
  const fd = new FormData(); fd.append("file", file);
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
    method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("anx_token") || ""}` }, body: fd,
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Upload failed"); }
  const d = await res.json();
  return `${process.env.REACT_APP_BACKEND_URL}${d.url}`;
}

function LogoDropzone({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const doFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file (PNG, JPG or WEBP)."); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo too large — please keep it under 2 MB."); return; }
    setBusy(true);
    try { onChange(await uploadImage(f)); toast.success("Logo uploaded"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50/60" data-testid="fet-logo-dropzone"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); doFile(e.dataTransfer.files?.[0]); }}>
      <div className="flex items-center gap-4">
        <div className="w-44 h-20 bg-white border border-slate-200 rounded flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={value} alt="Company logo" className="max-h-full max-w-full object-contain" /> : <span className="text-slate-400 text-xs">No logo yet</span>}
        </div>
        <div className="flex-1">
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#123a63] text-white text-sm font-bold cursor-pointer hover:brightness-110">
            <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Drop logo here or choose file"}
            <input type="file" accept="image/*" className="hidden" data-testid="fet-logo-input" onChange={(e) => doFile(e.target.files?.[0])} />
          </label>
          {value && <button type="button" onClick={() => onChange("")} data-testid="fet-logo-remove" className="ml-3 text-xs text-rose-600 font-semibold hover:underline">Remove</button>}
          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">Best results: <b>PNG</b> with a transparent background (JPG/WEBP also fine), landscape around <b>600×200px (3:1)</b>, under <b>1&nbsp;MB</b>. This appears top-left on your invoices, quotes, statements & delivery dockets.</p>
        </div>
      </div>
    </div>
  );
}

function RowEditor({ table, row, onClose, onSaved }) {
  const isNew = !row;
  const isCompany = table.id === "company_setup";
  const [vals, setVals] = useState(() => {
    const base = {}; table.columns.forEach((c) => { base[c] = row ? (row[c] ?? "") : ""; });
    if (isCompany && !("logo_url" in base)) base.logo_url = row ? (row.logo_url ?? "") : "";
    return base;
  });
  const [busy, setBusy] = useState(false);
  // On company_setup, the logo is handled by the drop-zone and the old binary "Logo" blob is left untouched.
  const hidden = isCompany ? ["logo_url", "Logo"] : [];
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...vals };
      if (isCompany) delete payload.Logo; // never resend the huge legacy OLE blob
      if (isNew) await api("POST", `/config/tables/${table.id}/row`, payload);
      else await api("POST", `/config/tables/${table.id}/update`, { original: row, updated: payload });
      toast.success(isNew ? "Row added" : "Saved");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="fet-row-editor">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 bg-[#123a63] text-white rounded-t-xl">
          <h3 className="font-bold">{isNew ? "Add" : "Edit"} — {table.label}</h3>
          <button onClick={onClose} data-testid="fet-row-close" className="hover:bg-white/20 rounded p-1"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-auto grid gap-3">
          {isCompany && (
            <div className="grid gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Company Logo (white-label)</span>
              <LogoDropzone value={vals.logo_url} onChange={(u) => setVals((v) => ({ ...v, logo_url: u }))} />
            </div>
          )}
          {table.columns.filter((c) => !hidden.includes(c)).map((c) => (
            <label key={c} className="grid gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{c}{c === table.pk ? " (key)" : ""}</span>
              <input value={vals[c]} onChange={(e) => setVals((v) => ({ ...v, [c]: e.target.value }))}
                data-testid={`fet-field-${c}`}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm focus:border-[#2f5fae] focus:ring-1 focus:ring-[#2f5fae] outline-none" />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-300 text-sm font-semibold text-slate-600">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="fet-row-save" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded bg-[#123a63] text-white text-sm font-bold disabled:opacity-50"><Save className="h-4 w-4" /> Save</button>
        </div>
      </div>
    </div>
  );
}

function TableEditor({ tid, label, onBack, onExit }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [scripts, setScripts] = useState(false);

  const load = useCallback(async (query = "") => {
    try { setData(await api("GET", `/config/tables/${tid}?q=${encodeURIComponent(query)}&limit=300`)); }
    catch (e) { toast.error(e.message); }
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const del = async (row) => {
    if (!window.confirm("Delete this row? This cannot be undone.")) return;
    try { await api("POST", `/config/tables/${tid}/delete`, { original: row }); toast.success("Row deleted"); load(q); }
    catch (e) { toast.error(e.message); }
  };

  if (!data) return <p className="text-slate-500 py-10 text-center">Loading {label}…</p>;
  const cols = data.columns.filter((c) => !(tid === "company_setup" && c === "Logo"));
  const table = { id: tid, label, pk: data.pk, columns: cols };
  return (
    <div className="space-y-3" data-testid="fet-table-editor">
      {scripts && <DocumentScripts onClose={() => setScripts(false)} />}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={onBack} data-testid="fet-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Toolbox</button>
        {onExit && <button onClick={onExit} data-testid="fet-editor-switchboard" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border-l border-slate-200 pl-3">Switchboard</button>}
        <h2 className="font-display font-bold text-lg text-[#123a63]">{label}</h2>
        <span className="text-xs text-slate-400">{data.total} rows</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)}
              placeholder="Search…" data-testid="fet-search"
              className="pl-8 pr-2 py-1.5 border border-slate-300 rounded text-sm w-48 focus:border-[#2f5fae] outline-none" />
          </div>
          <button onClick={() => load(q)} className="px-3 py-1.5 rounded border border-slate-300 text-sm font-semibold text-slate-600">Search</button>
          {tid === "company_setup" && <button onClick={() => setScripts(true)} data-testid="fet-company-scripts" className="px-3 py-1.5 rounded bg-[#2a9fd6] text-white text-sm font-bold">SCRIPTS</button>}
          <button onClick={() => setEditing(null)} data-testid="fet-add-row" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-bold"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-auto max-h-[calc(100vh-230px)]">
        <table className="w-full text-sm">
          <thead className="bg-[#123a63] text-white sticky top-0">
            <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2 font-bold whitespace-nowrap">{c}</th>)}
              <th className="px-3 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} data-testid={`fet-row-${i}`} className="border-b border-slate-100 hover:bg-sky-50">
                {cols.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap max-w-[260px] truncate" title={r[c]}>{r[c]}</td>)}
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)} data-testid={`fet-edit-${i}`} className="text-[#2f5fae] hover:bg-sky-100 rounded p-1"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} data-testid={`fet-delete-${i}`} className="text-rose-600 hover:bg-rose-100 rounded p-1 ml-1"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center text-slate-400 py-8">No rows.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing !== undefined && <RowEditor table={table} row={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(q); }} />}
    </div>
  );
}

export default function FieldEditToolbox({ onExit }) {
  const [tables, setTables] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);
  useEffect(() => { api("GET", "/config/tables").then(setTables).catch((e) => toast.error(e.message)); }, []);

  if (open) return (
    <div className="p-4">
      {open.id === "employee"
        ? <EmployeeEditor onBack={() => setOpen(null)} onExit={onExit} />
        : <TableEditor tid={open.id} label={open.label} onBack={() => setOpen(null)} onExit={onExit} />}
    </div>
  );
  return (
    <div className="p-6" data-testid="field-edit-toolbox">
      <div className="flex items-center gap-3 mb-1">
        <Wrench className="h-6 w-6 text-[#123a63]" />
        <h1 className="font-display font-bold text-2xl text-[#123a63]">Field Edit Toolbox</h1>
        {onExit && (
          <button onClick={onExit} data-testid="fet-switchboard" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-slate-200 text-sm font-bold text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Switchboard
          </button>
        )}
      </div>
      <p className="text-slate-500 text-sm mb-4">Add, edit or remove any lookup used across the software — every field is editable, just like Spa Buddy.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="fet-forms">
        {FORMS.map(([id, label, bg]) => (
          <button key={id} onClick={() => setForm(id)} data-testid={`fet-form-${id}`} style={{ backgroundColor: bg }}
            className="rounded-xl px-4 py-4 text-white font-black text-[14px] shadow-md ring-2 ring-white/40 hover:brightness-110 hover:-translate-y-0.5 transition-transform">{label}</button>
        ))}
      </div>
      {form && <FormPopup id={form} onClose={() => setForm(null)} />}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {(tables || []).map((t, i) => (
          <button key={t.id} onClick={() => setOpen(t)} data-testid={`fet-tile-${t.id}`}
            className="text-left rounded-xl px-4 py-3 text-white font-bold shadow hover:brightness-110 transition-[filter]"
            style={{ backgroundColor: TILE_COLORS[i % TILE_COLORS.length] }}>
            <div className="text-[13px] leading-tight">{t.id === "employee" ? "Add / Edit Employee" : t.label}</div>
            <div className="text-[11px] font-medium opacity-80 mt-1">{t.count} rows</div>
          </button>
        ))}
        {!tables && <p className="text-slate-400 col-span-full py-8 text-center">Loading toolbox…</p>}
      </div>
    </div>
  );
}
